'use client';

import { useEffect, useState } from 'react';

import { Button } from '~/components/ui/button';
import { Trans } from '~/components/ui/trans';

type PublicPropertyAuthState = {
  isSignedIn?: boolean;
  failed: boolean;
  attempt: number;
};

export function usePublicPropertyAuth() {
  const [state, setState] = useState<PublicPropertyAuthState>({
    failed: false,
    attempt: 0,
  });

  useEffect(() => {
    const controller = new AbortController();

    void fetch('/api/parcel-watch/public-property/auth', {
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) throw new Error('Sign-in status unavailable');
        const value = (await response.json()) as { isSignedIn?: unknown };
        if (typeof value.isSignedIn !== 'boolean') throw new Error('Sign-in status unavailable');
        if (!controller.signal.aborted)
          setState((current) => ({
            ...current,
            failed: false,
            isSignedIn: value.isSignedIn as boolean,
          }));
      })
      .catch(() => {
        if (!controller.signal.aborted) setState((current) => ({ ...current, failed: true }));
      });

    return () => controller.abort();
  }, [state.attempt]);

  return {
    ...state,
    retry: () =>
      setState((current) => ({
        ...current,
        failed: false,
        attempt: current.attempt + 1,
      })),
  };
}

export function PublicPropertyAuthStatus({ onRetry }: { onRetry: () => void }) {
  return (
    <div
      className="mx-auto mt-3 flex max-w-6xl items-center justify-between gap-3 rounded-xl border p-4 text-sm"
      role="status"
      data-test="property-auth-status"
    >
      <Trans i18nKey="parcelWatch.property.authUnavailable" />
      <Button type="button" variant="outline" size="sm" onClick={onRetry}>
        <Trans i18nKey="parcelWatch.common.retry" />
      </Button>
    </div>
  );
}
